import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { updateUserProfile } from '@/api/users';
import {
  FALLBACK_CURRENCIES_DATA,
  getDefaultWeekStartDay,
  getBrowserLocale,
  normalizeMonthStartDay,
  normalizeWeekStartDay,
  MIN_MONTH_START_DAY,
  MAX_MONTH_START_DAY,
} from '@sharecost/shared';
import type { WeekStartDay } from '@sharecost/shared';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18next from '@/i18n';

// 4 Jan 2026 is a Sunday; offsetting by the weekday index gives a date on that weekday
const weekdayName = (day: WeekStartDay) =>
  new Date(2026, 0, 4 + day).toLocaleDateString(i18next.language || getBrowserLocale(), { weekday: 'long' });

const WEEK_START_CHOICES: WeekStartDay[] = [1, 0, 6];
const MONTH_START_CHOICES = Array.from(
  { length: MAX_MONTH_START_DAY - MIN_MONTH_START_DAY + 1 },
  (_, index) => MIN_MONTH_START_DAY + index,
);
// <select> values are strings; 'auto' = follow the browser locale
const WEEK_START_AUTO = 'auto';

export function EditProfilePage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['settings']);
  const { user, profile, refreshProfile } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [defaultCurrency, setDefaultCurrency] = useState('');
  const [weekStart, setWeekStart] = useState<string>(WEEK_START_AUTO);
  const [monthStartDay, setMonthStartDay] = useState<number>(normalizeMonthStartDay(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const sortedCurrencies = useMemo(() => {
    return [...FALLBACK_CURRENCIES_DATA].sort((a, b) => {
      if (a.code === defaultCurrency) return -1;
      if (b.code === defaultCurrency) return 1;
      return a.code.localeCompare(b.code);
    });
  }, [defaultCurrency]);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name ?? '');
      setDefaultCurrency(profile.default_currency ?? 'CZK');
      const storedWeekStart = normalizeWeekStartDay(profile.week_start_day);
      setWeekStart(storedWeekStart === null ? WEEK_START_AUTO : String(storedWeekStart));
      setMonthStartDay(normalizeMonthStartDay(profile.month_start_day));
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !displayName.trim()) return;

    setSaving(true);
    setError('');

    try {
      await updateUserProfile(user.id, {
        name: displayName.trim(),
        defaultCurrency: defaultCurrency || undefined,
        weekStartDay: weekStart === WEEK_START_AUTO ? null : Number(weekStart),
        monthStartDay,
      });
      await refreshProfile();
      navigate('/profile');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('profile:userError.updateProfile'));
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-1 text-sm text-text-tertiary hover:text-text-secondary"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('web:editProfilePage.backToProfile')}
      </button>

      <h1 className="text-2xl font-bold text-text-primary mb-6">{t('profile:menu.editProfile')}</h1>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary">
        <div>
          <label className="block text-sm font-medium text-text-secondary">{t('web:common.displayName')}</label>
          <input
            type="text"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            required
            className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary">{t('groups:createModal.field.currency')}</label>
          <select
            value={defaultCurrency}
            onChange={e => setDefaultCurrency(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border-secondary bg-surface-primary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          >
            {sortedCurrencies.map(c => (
              <option key={c.code} value={c.code}>
                {c.code} ({c.symbol})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary">{t('periods.weekStart.label')}</label>
          <select
            value={weekStart}
            onChange={e => setWeekStart(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border-secondary bg-surface-primary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          >
            <option value={WEEK_START_AUTO}>
              {t('periods.weekStart.automatic', { day: weekdayName(getDefaultWeekStartDay(getBrowserLocale())) })}
            </option>
            {WEEK_START_CHOICES.map(day => (
              <option key={day} value={String(day)}>
                {weekdayName(day)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary">{t('periods.monthStart.label')}</label>
          <select
            value={monthStartDay}
            onChange={e => setMonthStartDay(normalizeMonthStartDay(Number(e.target.value)))}
            className="mt-1 w-full rounded-lg border border-border-secondary bg-surface-primary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          >
            {MONTH_START_CHOICES.map(day => (
              <option key={day} value={day}>
                {t('periods.monthStart.day', { day })}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-text-tertiary">{t('periods.monthStart.description')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary">{t('auth:form.placeholder.email')}</label>
          <input
            type="email"
            value={profile.email ?? ''}
            disabled
            className="mt-1 w-full rounded-lg border border-border-primary bg-surface-secondary px-3 py-2 text-sm text-text-tertiary"
          />
          <p className="mt-1 text-xs text-text-tertiary">
            {t('web:editProfilePage.emailCannotBeChangedHere')}
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 rounded-lg border border-border-secondary px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover"
          >
            {t('common:button.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving || !displayName.trim()}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('expenses:form.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
